import React from 'react'
import PropTypes from 'prop-types'
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Spinner,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import WebDrawer from '../../../components/web-drawer'
import {FormattedMessage} from 'react-intl'
import {useOptimizelyPage} from '../../../hooks/use-optimizely-page'
import {ChevronDownIcon} from '../../../components/icons'

const FAQ_KEYWORD_REGEX = /(gift\s*cards?|e-?\s*gift\s*cards?|e-?\s*cards?)/i

const getNodeText = (node = {}) => `${node?.Title || ''} ${node?.Text || ''}`.trim()

const getCollapsibleContent = (node = {}) =>
    node?.BulletItems || node?.Description || node?.Content || node?.Text || ''

const collectBlocks = (input, acc = []) => {
    if (!input) return acc

    if (Array.isArray(input)) {
        input.forEach((entry) => collectBlocks(entry, acc))
        return acc
    }

    if (typeof input !== 'object') return acc

    const expanded = input?.ContentLink?.Expanded
    if (expanded) {
        collectBlocks(expanded, acc)
        return acc
    }

    if (input?.__typename) {
        acc.push(input)
    }

    Object.keys(input).forEach((key) => {
        if (key === 'ContentLink') return
        collectBlocks(input[key], acc)
    })

    return acc
}

const isSectionHeading = (block) => {
    if (!block || block?.IsCollapsible) return false
    return Boolean(getNodeText(block))
}

const isCollapsibleFaq = (block) => Boolean(block?.IsCollapsible && block?.Title)

const mapToQuestion = (block) => ({
    title: block?.Title || '',
    content: getCollapsibleContent(block)
})

const getGiftCardQuestions = (content) => {
    const blocks = collectBlocks(content, [])
    const collapsibleFaqs = blocks.filter(isCollapsibleFaq)

    const directMatches = collapsibleFaqs
        .filter((block) =>
            FAQ_KEYWORD_REGEX.test(`${block?.Title || ''} ${getCollapsibleContent(block)}`)
        )
        .map(mapToQuestion)

    if (directMatches.length) return directMatches

    const startIndex = blocks.findIndex((block) => FAQ_KEYWORD_REGEX.test(getNodeText(block)))
    if (startIndex === -1) return []

    const sectionQuestions = []
    for (let i = startIndex + 1; i < blocks.length; i += 1) {
        const block = blocks[i]

        if (isSectionHeading(block) && sectionQuestions.length) {
            break
        }

        if (isCollapsibleFaq(block)) {
            sectionQuestions.push(mapToQuestion(block))
        }
    }

    return sectionQuestions
}

const GiftCardFaq = ({isOpen, onClose}) => {
    const {data: faqPageData, isLoading: isFaqLoading} = useOptimizelyPage('faq')
    const pageContent = faqPageData?.page || faqPageData
    const giftCardQuestions = getGiftCardQuestions(pageContent)

    return (
        <WebDrawer
            isOpen={isOpen}
            onClose={onClose}
            size="85vh"
            title={
                <Text as="span" textStyle="webH3">
                    <FormattedMessage
                        id="gift_card.faq.title"
                        defaultMessage="Frequently asked questions"
                    />
                </Text>
            }
        >
            <Box pb={4}>
                {isFaqLoading ? (
                    <Box py={10} textAlign="center">
                        <Spinner />
                    </Box>
                ) : giftCardQuestions.length ? (
                    <Accordion allowMultiple defaultIndex={[0]}>
                        {giftCardQuestions.map((item, index) => (
                            <AccordionItem
                                key={`gift-card-faq-${index}`}
                                borderTop="none"
                                borderBottom={index === giftCardQuestions.length - 1 ? 'none' : '0.5px solid'}
                                borderColor="webTheme.strokeDefault"
                            >
                                <AccordionButton py={3}>
                                    <Box as="span" flex="1" textAlign="left" textStyle={'bodySmRegular'}>
                                        {item.title}
                                    </Box>
                                    <AccordionIcon as={ChevronDownIcon} />
                                </AccordionButton>
                                <AccordionPanel pb={4}>
                                    <Box
                                        textStyle={'bodySmRegular'}
                                        sx={{
                                            '& a': {textDecoration: 'underline'},
                                            '& ul, & ol': {pl: 4, my: 3}
                                        }}
                                        dangerouslySetInnerHTML={{__html: item.content}}
                                    />
                                </AccordionPanel>
                            </AccordionItem>
                        ))}
                    </Accordion>
                ) : (
                    <Text textStyle={'bodySmRegular'}>
                        <FormattedMessage
                            id="gift_card.faq.empty"
                            defaultMessage="No gift card FAQs are available right now."
                        />
                    </Text>
                )}
            </Box>
        </WebDrawer>
    )
}

GiftCardFaq.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
}

export default GiftCardFaq
